import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Drawer, Popconfirm, Table, Tag, Typography, type TableColumnsType } from 'antd'
import { TableRowActions, type TableRowAction } from '@/components/table-row-actions.tsx'
import { formatShanghaiDateTime } from '@/const/date.ts'
import { DRAWER_SIZE } from '@/const/drawer.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { CONTRACT_TEMPLATE_VERSION_STATUS } from './contract-labels.ts'
import type { ContractTemplateRecord } from './contract-template-editor-page.tsx'

type TemplateVersionRow = {
  id: number
  version: number
  status: keyof typeof CONTRACT_TEMPLATE_VERSION_STATUS
  variableKeys: string[]
  changeNote: string | null
  createdAt: Date
}

export function ContractTemplateVersionsDrawer({
  template,
  onClose,
  onEditDraft,
  onViewVersion
}: {
  template?: ContractTemplateRecord
  onClose: () => void
  onEditDraft: (template: ContractTemplateRecord) => void
  onViewVersion: (template: ContractTemplateRecord, versionId: number) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const versions = useQuery({
    ...trpc.contract.template.versions.queryOptions({ templateId: template?.id ?? 0 }),
    enabled: Boolean(template)
  })
  const publish = useMutation(trpc.contract.template.publishVersion.mutationOptions())
  const restore = useMutation(trpc.contract.template.restoreVersion.mutationOptions())

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries(trpc.contract.template.versions.queryFilter({ templateId: template?.id ?? 0 })),
      queryClient.invalidateQueries(trpc.contract.template.list.queryFilter())
    ])
  }

  const publishSelectedVersion = async (row: TemplateVersionRow) => {
    try {
      await publish.mutateAsync({ id: row.id })
      void message.success(`模板 v${row.version} 已发布`)
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '模板版本发布失败'))
    }
  }

  const restoreVersion = async (row: TemplateVersionRow) => {
    try {
      const draft = await restore.mutateAsync({ id: row.id })
      void message.success(`已基于 v${row.version} 生成草稿 v${draft.version}，请审核后发布`)
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '合同模板版本回退失败'))
    }
  }

  const confirmPublishSelectedVersion = (row: TemplateVersionRow) => {
    modal.confirm({
      onOk: () => publishSelectedVersion(row),
      title: '发布该模板版本？'
    })
  }

  const confirmRestoreVersion = (row: TemplateVersionRow) => {
    modal.confirm({
      content: '系统会把该历史版本复制为新的草稿，不会修改历史版本；审核后还需要发布才会成为当前版本。',
      onOk: () => restoreVersion(row),
      title: `回退并生成 v${row.version} 草稿？`
    })
  }

  const columns: TableColumnsType<TemplateVersionRow> = [
    {
      dataIndex: 'id',
      title: 'ID',
      width: '7rem',
      render: (id: number) => <Typography.Text copyable>{id}</Typography.Text>
    },
    { dataIndex: 'version', title: '版本', width: '5rem', render: (version: number) => `v${version}` },
    {
      dataIndex: 'status',
      title: '状态',
      width: '8rem',
      render: (status: keyof typeof CONTRACT_TEMPLATE_VERSION_STATUS) => (
        <Tag color={CONTRACT_TEMPLATE_VERSION_STATUS[status].color}>
          {CONTRACT_TEMPLATE_VERSION_STATUS[status].label}
        </Tag>
      )
    },
    {
      dataIndex: 'variableKeys',
      title: '动态字段',
      width: '7rem',
      render: (variableKeys: string[]) => `${variableKeys.length} 个`
    },
    {
      dataIndex: 'changeNote',
      title: '变更说明',
      ellipsis: true,
      render: (note: string | null) => note || '—'
    },
    {
      dataIndex: 'createdAt',
      title: '创建时间',
      width: '11rem',
      render: (date: Date) => formatShanghaiDateTime(date)
    },
    {
      fixed: 'right',
      align: 'center',
      key: 'action',
      title: '操作',
      width: '10rem',
      render: (_: unknown, row: TemplateVersionRow) => {
        const actions: TableRowAction[] = [
          {
            key: 'view',
            label: '查看',
            onClick: () => {
              if (template) {
                onViewVersion(template, row.id)
              }
            }
          }
        ]
        if (row.status === 'draft') {
          actions.push(
            {
              key: 'edit',
              label: '编辑草稿',
              onClick: () => {
                if (template) {
                  onEditDraft(template)
                }
              }
            },
            {
              key: 'publish',
              label: '发布',
              onClick: () => confirmPublishSelectedVersion(row),
              render: (
                <Popconfirm onConfirm={() => void publishSelectedVersion(row)} title="发布该模板版本？">
                  <Authorized permissions={['contract.template.publish']}>
                    <Button size="small" type="link">
                      发布
                    </Button>
                  </Authorized>
                </Popconfirm>
              )
            }
          )
        } else if (template && row.version !== template.currentVersion) {
          actions.push({
            key: 'restore',
            label: '回退',
            onClick: () => confirmRestoreVersion(row),
            render: (
              <Popconfirm
                description="系统会把该历史版本复制为新的草稿，不会修改历史版本；审核后还需要发布才会成为当前版本。"
                onConfirm={() => void restoreVersion(row)}
                title={`回退并生成 v${row.version} 草稿？`}
              >
                <Button size="small" type="link">
                  回退
                </Button>
              </Popconfirm>
            )
          })
        }
        return <TableRowActions resource="contract.template" actions={actions} />
      }
    }
  ]

  return (
    <Drawer
      destroyOnHidden
      onClose={onClose}
      open={Boolean(template)}
      title={template ? `${template.name} · 版本历史` : '版本历史'}
      size={DRAWER_SIZE}
    >
      <Typography.Paragraph type="secondary">
        版本历史仅展示版本元数据。点击“查看”会进入对应版本的只读页面；回退会生成一个新的草稿，不会改写历史版本或已生成合同。
      </Typography.Paragraph>
      {versions.isError ? (
        <Alert
          action={
            <Button onClick={() => void versions.refetch()} size="small">
              重试
            </Button>
          }
          className="mb-4"
          showIcon
          title={getErrorMessage(versions.error, '版本历史加载失败')}
          type="error"
        />
      ) : null}
      <Table<TemplateVersionRow>
        columns={columns}
        dataSource={versions.data ?? []}
        loading={versions.isLoading || publish.isPending || restore.isPending}
        pagination={false}
        rowKey="id"
        scroll={{ x: '58rem' }}
        size="small"
      />
    </Drawer>
  )
}
